import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDiveDeeper } from "./api";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
// Custom component to render HTML without wrapper
const RawHTMLContent = ({ html }) => {
  const ref = useRef();

  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = html;
    }
  }, [html]);

  return <span ref={ref} style={{ display: 'contents' }} />;
};

// Register the SplitText plugin
gsap.registerPlugin(SplitText);

// Helper function to get current slides per view based on breakpoints
const getCurrentSlidesPerView = () => {
  const width = window.innerWidth;
  if (width >= 1200) return 4;
  if (width >= 1024) return 3;
  if (width >= 768) return 2;
  return 1;
};

// Calculate slide widths and set CSS custom properties
const calculateSlideWidths = (carouselElement) => {
  if (!carouselElement) return;

  const spacer = carouselElement.closest('.dive-deeper').querySelector('.dive-deeper__spacer');
  if (!spacer) return;

  const containerWidth = spacer.offsetWidth;
  const windowWidth = window.innerWidth;

  // Don't calculate for mobile (below 768px)
  if (windowWidth < 768) return;

  // Determine spacing based on screen size
  let spaceBetween = 20;
  if (windowWidth >= 1680) {
    spaceBetween = 32;
  } else if (windowWidth >= 1280) {
    spaceBetween = 24;
  }

  // Always 4 slides as requested
  const slidesPerContainer = 4;

  // Calculate slide width
  const totalSpacing = spaceBetween * (slidesPerContainer - 1);
  const availableWidth = containerWidth - totalSpacing;
  const slideWidth = availableWidth / slidesPerContainer;

  // Calculate featured width (double width + spacing)
  const featuredWidth = (slideWidth * 2) + spaceBetween;

  // Set CSS custom properties
  carouselElement.style.setProperty('--slide-width', `${slideWidth}px`);
  carouselElement.style.setProperty('--featured-width', `${featuredWidth}px`);
  carouselElement.style.setProperty('--slides-per-view', slidesPerContainer.toString());
};

// Get slides offset based on screen size
const getSlidesOffset = (windowWidth) => {
  return windowWidth >= 1280 ? 144 : 20;
};

const DiveDeeper = ({ config, filters, exclude_featured }) => {
  const [selectedTopics, setSelectedTopics] = useState(new Set())
  const [currentSlidesPerView, setCurrentSlidesPerView] = useState(getCurrentSlidesPerView())
  const carouselRef = useRef(null)
  const swiperRef = useRef(null)

  const { data: diveDeeperData, isLoading, error } = useQuery({
    queryKey: ['diveDeeper', config.post_type, filters, config.exclude_featured, config.terms],
    queryFn: () => fetchDiveDeeper({
      post_type: config.post_type,
      filters,
      exclude_featured: config.exclude_featured,
      terms: config.terms
    }),
    staleTime: 2 * 60 * 1000,
    enabled: !!filters && Object.keys(filters).length > 0 && !!config.terms && config.terms.length > 0,
  })

  const handleTopicClick = useCallback((topic) => {
    setSelectedTopics(prev => {
      const newSet = new Set(prev)
      if (newSet.has(topic)) {
        newSet.delete(topic)
      } else {
        newSet.add(topic)
      }
      return newSet
    })
  }, [])

  const filteredTopics = diveDeeperData?.topics || []

  const filteredCards = selectedTopics.size > 0
    ? (diveDeeperData?.cards || []).filter(card => {
        return Array.from(selectedTopics).some(selectedTopic =>
          card.topics && card.topics.includes(selectedTopic)
        )
      })
    : (diveDeeperData?.cards || [])

  useEffect(() => {
    if (diveDeeperData) {
      const elements = document.querySelectorAll('.has-indentation');
      elements.forEach((element) => {
        if (element.dataset.indentationApplied !== 'true') {
          try {
            const split = new SplitText(element, { type: "lines" });
            const lines = split.lines;

            if (lines.length > 0) {
              // Just append the lines directly (they're already divs from SplitText)
              lines.forEach((line, index) => {
                element.appendChild(line);
              });
              element.dataset.indentationApplied = 'true';
            }
          } catch (error) {
            console.warn('Failed to apply indentation:', error);
          }
        }
      });

      // Calculate slide widths after data is loaded
      setTimeout(() => {
        calculateSlideWidths(carouselRef.current);
      }, 300);
    }
  }, [diveDeeperData]);

  // Handle window resize to update slides per view
  useEffect(() => {
    const handleResize = () => {
      setCurrentSlidesPerView(getCurrentSlidesPerView());
      calculateSlideWidths(carouselRef.current);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!diveDeeperData || !filters || Object.keys(filters).length === 0) {
    return null
  }

  if (!filteredCards || filteredCards.length === 0) {
    return null
  }

  return (
    <div className="dive-deeper grid bg-6">
      <div className="dive-deeper__heading">
        <h2 className="dive-deeper__title">
          <div className="has-indentation" dangerouslySetInnerHTML={{__html: config.dive_deeper.heading}} />

        </h2>
        <div className="dive-deeper__description">
          <hr className="wp-block-separator has-text-color has-yellow-color has-alpha-channel-opacity has-yellow-background-color has-background is-style-short" />
          <p className="h5">{config.dive_deeper.description}</p>
        </div>
      </div>
      {filteredTopics.length > 0 && (
        <div className="dive-deeper__tags actions">
          {filteredTopics.map((topic, index) => (
            <div key={index} className="type-outline">
            <button
              className={`${selectedTopics.has(topic) ? 'is-active' : ''}`}
              onClick={() => handleTopicClick(topic)}
            >
              {topic}
            </button>
            </div>
          ))}
        </div>
      )}

              <div className="dive-deeper__carousel" ref={carouselRef}>

            <div className="dive-deeper__carousel--top">
              <div className="dive-deeper__navigation swiper-arrows">
                <button className="swiper-button-prev" aria-label="Previous">
                  <svg width="42" height="42" fill="currentColor"><path d="M18.047 12.789L25.156 21l-7.109 8.211-1.203-1.422L22.722 21l-5.878-6.789 1.203-1.422z"/></svg>

                </button>
                <button className="swiper-button-next" aria-label="Next">
                  <svg width="42" height="42" fill="currentColor"><path d="M18.047 12.789L25.156 21l-7.109 8.211-1.203-1.422L22.722 21l-5.878-6.789 1.203-1.422z"/></svg>
                </button>
              </div>
            </div>

        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={window.innerWidth >= 1680 ? 32 : window.innerWidth >= 1280 ? 24 : 20}
          slidesPerView="auto"
          slidesOffsetBefore={getSlidesOffset(window.innerWidth)}
          slidesOffsetAfter={getSlidesOffset(window.innerWidth)}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          pagination={{
            el: '.swiper-pagination',
            clickable: true
          }}
          breakpoints={{
            1280: {
              spaceBetween: 24,
              slidesOffsetBefore: getSlidesOffset(1280),
              slidesOffsetAfter: getSlidesOffset(1280),
            },
            1680: {
              spaceBetween: 32,
              slidesOffsetBefore: getSlidesOffset(1680),
              slidesOffsetAfter: getSlidesOffset(1680),
            },
          }}
        >
          {filteredCards.map((card) => (
            <SwiperSlide key={card.id} className="dive-deeper__card">
              <div
                className="card card--dive-deeper"
                data-card-type="insights"
                dangerouslySetInnerHTML={{__html: card.html}}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="dive-deeper__carousel--bottom">
          <div className="swiper-pagination"></div>
        </div>
      </div>
      <div className="dive-deeper__spacer"></div>
    </div>
  )
}

export default DiveDeeper
